
import React, { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";

import { CartProduct } from "../../assets/icons/icons";
import { Link } from "react-router-dom";
import UseScrollAnimation from "../../hooks/UseScrollAnimation";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Autoplay } from "swiper/modules";

export default function PopularProduct() {
  const { t, i18n } = useTranslation();

  UseScrollAnimation();

  const [products, setProducts] = useState([]);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(
          "https://laravel-api.aj-mart.com/api/products"
        );

        console.log("Fetched Products:", response.data.products);

        const fetchedProducts = Array.isArray(response.data.products)
          ? response.data.products
          : [];

        // ترتيب حسب آخر منتج مضاف
        // أعلى product_id يظهر أولاً
        const latestProducts = [...fetchedProducts].sort(
          (a, b) =>
            Number(b.product_id || 0) -
            Number(a.product_id || 0)
        );

        setProducts(latestProducts);

      } catch (error) {
        console.error("Error fetching products:", error);
        setProducts([]);
      }
    };

    fetchProducts();
  }, []);

  const getLocalizedText = (key) => {
    const value =
      i18n.language === "ar"
        ? key.A
        : key.E;

    return value ?? "";
  };

  const renderProduct = (product, index) => {
    const priceAfterDiscount = (
      (Number(product.product_price) || 0) *
      (1 - (Number(product.product_price_discount) || 0) / 100)
    ).toFixed(2);

    const inventoryClass =
      Number(product.product_stock_available) > 0
        ? "text-[#18AB00]"
        : "text-red-500";

    const isHovered = hoveredIndex === index;

    const productName =
      getLocalizedText({
        A: product.product_name_A,
        E: product.product_name_E,
      }) || "Unnamed Product";

    const productDesc =
      getLocalizedText({
        A: product.product_desc_A,
        E: product.product_desc_E,
      }) || "No description available";

    const descItems =
      i18n.language === "ar"
        ? productDesc.split("\\ة")
        : productDesc.split(/\\n|\n/);

    return (
      <SwiperSlide key={product.product_id}>
        <Link
          to={`/productdetails/${product.product_id}`}
          className="m-[4px] cursor-pointer flex flex-col w-[325px] h-[500px] rounded-md outline outline-[2px] outline-solid shadow-md relative"
          style={{ outlineColor: "#eee" }}
          onMouseEnter={() => setHoveredIndex(index)}
          onMouseLeave={() => setHoveredIndex(null)}
        >
          <div className="h-[230px] w-full relative">
            <img
              className="w-full h-full rounded-t-md object-cover"
              src={`https://laravel-api.aj-mart.com/storage/app/public/${
                product.product_main_img || "placeholder.jpg"
              }`}
              alt={productName}
            />

            {Number(product.product_price_discount) > 0 && (
              <div className="w-10 h-10 bg-[#1485a6] rounded-full text-white font-bold flex items-center justify-center absolute top-[10px] right-[10px]">
                {Number(product.product_price_discount)}%
              </div>
            )}
          </div>

          <div className="h-[2px] bg-[#666666]"></div>

          <div className="p-[1rem] py-[10px] flex flex-col gap-[10px] pb-5">
            <div className="flex items-center justify-between">
              <div className="text-[22px] font-bold">
                {productName.length > 50
                  ? `${productName.substring(0, 50)}...`
                  : productName}
              </div>

              <div className="w-[77px] h-[22px] bg-[#0095d5] text-white font-bold rounded-tr-[100px] rounded-bl-[100px] flex items-center justify-center text-[13px]">
                {t(product.product_status || "Unknown")}
              </div>
            </div>

            <ul className="text-[13px] text-[#666666] list-disc pl-[20px]">
              {descItems.map(
                (desc, idx) =>
                  desc.trim() && (
                    <li key={idx} className="text-[13px]">
                      {desc.trim().length > 150
                        ? `${desc.trim().substring(0, 150)}...`
                        : desc.trim()}
                    </li>
                  )
              )}
            </ul>

            <div className="flex justify-between">
              <div className="flex flex-col gap-[5px]">
                <span className="font-bold">
                  {priceAfterDiscount} {t("JOD")}
                </span>

                <span className="line-through">
                  {Number(product.product_price).toFixed(2)} {t("JOD")}
                </span>
              </div>

              <div className={`font-bold ${inventoryClass}`}>
                {t(
                  Number(product.product_stock_available) > 0
                    ? "In Stock"
                    : "Out of Stock"
                )}
              </div>
            </div>
          </div>

          <div
            className={`bg-[#1485a6] flex p-1.5 items-center justify-center gap-1.5 text-white font-bold rounded-tl-[20px] rounded-tr-[20px] absolute bottom-0 left-0 right-0 transition-opacity duration-300 ${
              isHovered ? "opacity-100" : "opacity-0"
            }`}
          >
            <CartProduct />
            {t("Add To Cart")}
          </div>
        </Link>
      </SwiperSlide>
    );
  };

  return (
    <section className="relative py-[40px]">
      <div className="container">

        <div className="btmAni30002 direction-text-ar page-title mb-[20px]">
          {t("Popular")} <span>{t("Products")}</span>
        </div>

        <Swiper
          modules={[Autoplay]}
          spaceBetween={20}
          slidesPerView={1}
          loop={products.length > 3}
          observer={true}
          observeParents={true}
          breakpoints={{
            640: { slidesPerView: 1 },
            768: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
          }}
          className="js-scroll fade-top w-100 h-[507px] p-[20px]"
          style={{ direction: "ltr" }}
        >
          {products.length > 0 ? (
            products.map((product, index) =>
              renderProduct(product, index)
            )
          ) : (
            <SwiperSlide>
              <div className="text-center text-gray-500">
                {t("No products available")}
              </div>
            </SwiperSlide>
          )}
        </Swiper>

        <div className="flex items-center justify-center my-[15px]">
          <Link
            className="btn js-scroll fade-top"
            to="/productgrid"
          >
            {t("Show More")}
          </Link>
        </div>

      </div>
    </section>
  );
}
